import { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { adminApi } from "services/api";
import { COURSE_OPTIONS } from "utils/courses";
import { BOOKING_STATUS_OPTIONS } from "utils/bookingStatuses";
import { lightDark } from "utils/lightDark";
import {
  PLACEHOLDERS,
  filterSummary,
  regionUnknownNote,
  plannedTotal,
  plannedSummary,
  skippedSummary,
  testSentMessage,
  sendIdFor,
  sendFailureNotice,
  unknownPlaceholdersNote,
} from "./helpers";

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCES = [
  {
    key: "landing_application",
    label: "Landing Page Applications",
    icon: "how_to_reg",
    color: "#1a73e8",
  },
  {
    key: "webinar_registration",
    label: "Webinar Registrations",
    icon: "live_tv",
    color: "#ea4335",
  },
  { key: "bootcamp_application", label: "Bootcamp Applications", icon: "school", color: "#34a853" },
  { key: "contact_submission", label: "Contact Submissions", icon: "email", color: "#fbbc04" },
  {
    key: "consultation",
    label: "Consultation Bookings",
    icon: "event",
    color: "#9c27b0",
    darkColor: "#ce93d8",
  },
  {
    key: "clarity_call_booking",
    label: "Clarity Call Bookings",
    icon: "support_agent",
    color: "#00acc1",
  },
];

const DATE_PRESETS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

const SOURCE_LABELS = Object.fromEntries(SOURCES.map((s) => [s.key, s.label]));

const SMS_LIMIT = 160;

function getDateRange(preset) {
  if (preset === "all" || preset === "custom") return { dateFrom: "", dateTo: "" };
  const days = { "7d": 7, "30d": 30, "90d": 90 }[preset] || 0;
  const today = new Date().toISOString().split("T")[0];
  const from = new Date(Date.now() - days * 86400000).toISOString().split("T")[0];
  return { dateFrom: from, dateTo: today };
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Filter controls share each row evenly on phones; desktop keeps their fixed widths.
const filterFlex = { flex: { xs: "1 1 130px", md: "0 1 auto" } };

// Sticky action bar buttons stretch to share the bar on phones (24px is the MD button padding).
const actionBarButtonSx = { flex: { xs: "1 1 auto", md: "0 1 auto" }, px: { xs: 1.5, md: 3 } };

function SectionCard({ title, icon, badge, children, action }) {
  return (
    <Card sx={{ mb: 2.5 }}>
      <MDBox p={{ xs: 2, md: 3 }}>
        <MDBox
          display="flex"
          flexWrap="wrap"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          mb={2.5}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "info.main", fontSize: "20px !important" }}>{icon}</Icon>
            <MDTypography variant="h6" fontWeight="medium">
              {title}
            </MDTypography>
            {badge !== undefined && (
              <Chip
                label={badge}
                size="small"
                color="info"
                sx={{ fontWeight: 700, fontSize: "0.7rem", height: 20 }}
              />
            )}
          </MDBox>
          {action}
        </MDBox>
        {children}
      </MDBox>
    </Card>
  );
}

function ConsentDot({ has }) {
  return (
    <Tooltip title={has ? "Consented" : "Not consented"}>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: has ? "#34a853" : "#dadce0",
        }}
      />
    </Tooltip>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function BulkMessage() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  // Audience
  const [sources, setSources] = useState([]);
  const [filter, setFilter] = useState({
    region: "all",
    course: "all",
    bookingStatuses: [],
    datePreset: "all",
    dateFrom: "",
    dateTo: "",
  });
  const [audience, setAudience] = useState({
    count: 0,
    channels: null,
    recipients: [],
    regionUnknown: {},
    loading: false,
  });
  const [showList, setShowList] = useState(false);
  const [excludedIds, setExcludedIds] = useState(new Set());

  // Channels
  const [channels, setChannels] = useState({ email: false, sms: false });
  const [respectConsent, setRespectConsent] = useState(true);

  // Content
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsBody, setSmsBody] = useState("");

  // Test send
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);

  // Preview dialog
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Confirm + send
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  // A send that got no reply may still be running — shown as a banner, not a vanishing toast.
  const [sendNotice, setSendNotice] = useState(null);
  // { fingerprint, id } of the last message sent; the server refuses an id it has seen.
  const sendIdRef = useRef(null);

  // Notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    icon: "check",
    title: "",
    content: "",
  });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const notify = (color, icon, title, content) =>
    setSnackbar({ open: true, color, icon, title, content });

  // Derived
  const activeChannels = Object.keys(channels).filter((k) => channels[k]);
  // Messages the send will make on the switched-on channels (server-counted, per address).
  const plannedCount = plannedTotal(audience.channels, activeChannels);
  const canSend =
    sources.length > 0 &&
    activeChannels.length > 0 &&
    (!channels.email || (emailSubject.trim() && emailBody.trim())) &&
    (!channels.sms || smsBody.trim());

  const filterPayload = useCallback(
    () => ({
      region: filter.region !== "all" ? filter.region : undefined,
      course: filter.course !== "all" ? filter.course : undefined,
      bookingStatuses:
        sources.includes("clarity_call_booking") && filter.bookingStatuses.length
          ? filter.bookingStatuses
          : undefined,
      dateFrom: filter.dateFrom || undefined,
      dateTo: filter.dateTo || undefined,
    }),
    [filter, sources]
  );

  const buildPayload = useCallback(
    () => ({
      sources,
      filter: filterPayload(),
      excludeIds: excludedIds.size > 0 ? [...excludedIds] : undefined,
      channels: activeChannels,
      subject: emailSubject,
      emailBody,
      smsBody,
      respectConsent,
    }),
    [
      sources,
      filterPayload,
      excludedIds,
      activeChannels,
      emailSubject,
      emailBody,
      smsBody,
      respectConsent,
    ]
  );

  // ── Live audience count (debounced) ──────────────────────────────────────
  // Exclusions belong to one audience: reset them whenever the sources or filters change.
  useEffect(() => {
    setExcludedIds(new Set());
  }, [sources, filterPayload]);

  // Re-counted when consent or exclusions change too, so the per-channel numbers on the button
  // and in the confirm dialog are exactly what the send will do.
  useEffect(() => {
    if (!sources.length) {
      setAudience({ count: 0, channels: null, recipients: [], regionUnknown: {}, loading: false });
      return;
    }
    setAudience((a) => ({ ...a, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const data = await adminApi.getAudience({
          sources,
          filter: filterPayload(),
          respectConsent,
          excludeIds: [...excludedIds],
        });
        setAudience({
          count: data.count,
          channels: data.channels || null,
          recipients: data.recipients,
          regionUnknown: data.regionUnknown || {},
          loading: false,
        });
      } catch {
        setAudience((a) => ({ ...a, loading: false }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [sources, filterPayload, respectConsent, excludedIds]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const toggleSource = (key) =>
    setSources((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));

  const handleFilterChange = (key, value) => {
    setFilter((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "datePreset" && value !== "custom") {
        const range = getDateRange(value);
        next.dateFrom = range.dateFrom;
        next.dateTo = range.dateTo;
      }
      return next;
    });
  };

  const toggleContact = (id) => {
    const sid = String(id);
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const allSelected = excludedIds.size === 0;
  const someExcluded = excludedIds.size > 0 && excludedIds.size < audience.recipients.length;
  const selectAll = () => setExcludedIds(new Set());
  const deselectAll = () => setExcludedIds(new Set(audience.recipients.map((r) => String(r._id))));

  const handlePreview = async () => {
    if (!sources.length) {
      notify("warning", "warning", "No audience", "Select at least one source first.");
      return;
    }
    setPreviewing(true);
    try {
      const data = await adminApi.previewBulkMessage(buildPayload());
      setPreviewData(data);
      setPreviewOpen(true);
    } catch (err) {
      notify("error", "error", "Preview failed", err.response?.data?.error || err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) {
      notify("warning", "warning", "No email", "Enter a test email address.");
      return;
    }
    if (!activeChannels.length) {
      notify("warning", "warning", "No channel", "Select email or SMS first.");
      return;
    }
    if (!channels.email) {
      notify(
        "warning",
        "warning",
        "Nothing sent",
        "Tests are sent only by email. Turn on Email to send a test, or use Preview to check the SMS text."
      );
      return;
    }
    setTestSending(true);
    try {
      const data = await adminApi.testBulkMessage({ ...buildPayload(), testEmail });
      const unknown = data.unknownPlaceholders?.length
        ? ` Unknown placeholders went out as typed: ${data.unknownPlaceholders.join(", ")}.`
        : "";
      notify(
        data.smsNotSent || unknown ? "warning" : "success",
        "check",
        "Test sent",
        testSentMessage(data, testEmail) + unknown
      );
    } catch (err) {
      notify("error", "error", "Test failed", err.response?.data?.error || err.message);
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setResults(null);
    setSendNotice(null);
    const payload = buildPayload();
    sendIdRef.current = sendIdFor(sendIdRef.current, payload);
    try {
      const data = await adminApi.sendBulkMessage({ ...payload, sendId: sendIdRef.current.id });
      setResults(data);
      notify(
        "success",
        "check",
        "Done",
        `${data.sent} sent · ${data.failed} failed · ${data.skipped} skipped`
      );
    } catch (err) {
      const notice = sendFailureNotice(err);
      setSendNotice(notice);
      const color = notice.severity === "error" ? "error" : "warning";
      notify(color, color, notice.title, notice.text);
    } finally {
      setSending(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={8} px={1}>
        {/* Header */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Bulk Message
          </MDTypography>
          <MDTypography variant="body2" color="secondary" mt={0.5}>
            Compose and send email or SMS to any audience with flexible filters and live recipient
            preview. Supports <code>{"{{placeholder}}"}</code> variables identical to the
            autoresponder system.
          </MDTypography>
        </MDBox>

        {/* Results banner */}
        <Collapse in={!!results}>
          {results && (
            <MDBox mb={3}>
              <Alert
                severity={results.failed > 0 ? "warning" : "success"}
                onClose={() => setResults(null)}
              >
                <strong>Send complete.</strong> {results.sent} sent &nbsp;·&nbsp; {results.failed}{" "}
                failed &nbsp;·&nbsp;
                {results.skipped} skipped &nbsp;·&nbsp; {results.total} total.
                {results.errors?.length > 0 && (
                  <MDBox
                    mt={1}
                    component="ul"
                    pl={2}
                    sx={{ fontSize: "0.72rem", color: "text.secondary", overflowWrap: "anywhere" }}
                  >
                    {results.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>
                        {e.contact} [{e.channel}]: {e.error}
                      </li>
                    ))}
                  </MDBox>
                )}
              </Alert>
            </MDBox>
          )}
        </Collapse>

        {sendNotice && (
          <MDBox mb={3}>
            <Alert severity={sendNotice.severity} onClose={() => setSendNotice(null)}>
              <strong>{sendNotice.title}.</strong> {sendNotice.text}
            </Alert>
          </MDBox>
        )}

        <Grid container spacing={3}>
          {/* ── LEFT COLUMN ── */}
          <Grid item xs={12} lg={8}>
            {/* STEP 1 — Audience */}
            <SectionCard
              title="Audience"
              icon="groups"
              badge={audience.loading ? "…" : audience.count}
              action={
                sources.length > 0 && (
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => setShowList((v) => !v)}
                    startIcon={<Icon>{showList ? "expand_less" : "people"}</Icon>}
                    disabled={audience.loading}
                  >
                    {showList ? "Hide list" : "View & select recipients"}
                  </MDButton>
                )
              }
            >
              {/* Source checkboxes */}
              <Grid container spacing={0.5} mb={2}>
                {SOURCES.map((source) => (
                  <Grid item xs={12} sm={6} key={source.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sources.includes(source.key)}
                          onChange={() => toggleSource(source.key)}
                          color="info"
                          size="small"
                        />
                      }
                      label={
                        <MDBox display="flex" alignItems="center" gap={0.75}>
                          <Icon
                            sx={{
                              fontSize: "16px !important",
                              color: lightDark(source.color, source.darkColor || source.color),
                            }}
                          >
                            {source.icon}
                          </Icon>
                          <MDTypography variant="button" fontWeight="regular">
                            {source.label}
                          </MDTypography>
                        </MDBox>
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              {/* Filters */}
              <MDBox display="flex" flexWrap="wrap" gap={2} mb={1.5}>
                {/* Region */}
                <FormControl size="small" sx={{ minWidth: 130, ...filterFlex }}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={filter.region}
                    label="Region"
                    onChange={(e) => handleFilterChange("region", e.target.value)}
                  >
                    <MenuItem value="all">All regions</MenuItem>
                    <MenuItem value="uk">UK only</MenuItem>
                    <MenuItem value="ng">Nigeria only</MenuItem>
                  </Select>
                </FormControl>

                {/* Course */}
                <FormControl size="small" sx={{ minWidth: 170, ...filterFlex }}>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={filter.course}
                    label="Course"
                    onChange={(e) => handleFilterChange("course", e.target.value)}
                  >
                    <MenuItem value="all">All courses</MenuItem>
                    {COURSE_OPTIONS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {sources.includes("clarity_call_booking") && (
                  <FormControl size="small" sx={{ minWidth: 220, ...filterFlex }}>
                    <InputLabel>Booking status</InputLabel>
                    <Select
                      multiple
                      value={filter.bookingStatuses}
                      label="Booking status"
                      onChange={(e) => handleFilterChange("bookingStatuses", e.target.value)}
                      renderValue={(selected) =>
                        selected.length ? selected.join(", ") : "All except cancelled"
                      }
                      displayEmpty
                    >
                      {BOOKING_STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          <Checkbox size="small" checked={filter.bookingStatuses.includes(s)} />
                          {s.toLowerCase().replace("_", " ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Date preset */}
                <FormControl size="small" sx={{ minWidth: 150, ...filterFlex }}>
                  <InputLabel>Joined</InputLabel>
                  <Select
                    value={filter.datePreset}
                    label="Joined"
                    onChange={(e) => handleFilterChange("datePreset", e.target.value)}
                  >
                    {DATE_PRESETS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Custom date range */}
                {filter.datePreset === "custom" && (
                  <>
                    <TextField
                      label="From"
                      type="date"
                      size="small"
                      value={filter.dateFrom}
                      onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, ...filterFlex }}
                    />
                    <TextField
                      label="To"
                      type="date"
                      size="small"
                      value={filter.dateTo}
                      onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, ...filterFlex }}
                    />
                  </>
                )}
              </MDBox>

              <MDTypography variant="caption" color="text" display="block" mb={1}>
                Course applies to webinar registrations, landing applications and clarity calls;
                other sources are unaffected. Region uses each contact&apos;s stored region or
                country, or else their phone&apos;s country code.
              </MDTypography>
              {filter.region !== "all" &&
                !audience.loading &&
                regionUnknownNote(audience.regionUnknown, SOURCE_LABELS) && (
                  <MDTypography
                    variant="caption"
                    display="block"
                    mb={1}
                    sx={{ color: "warning.main" }}
                  >
                    {regionUnknownNote(audience.regionUnknown, SOURCE_LABELS)}
                  </MDTypography>
                )}

              {/* Audience summary */}
              {sources.length > 0 && (
                <MDBox
                  display="flex"
                  alignItems="center"
                  flexWrap={{ xs: "wrap", md: "nowrap" }}
                  gap={1}
                  sx={{
                    background: "rgba(26,115,232,0.06)",
                    border: "1px solid rgba(26,115,232,0.15)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  {audience.loading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Icon sx={{ color: "info.main", fontSize: "18px !important" }}>people</Icon>
                  )}
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="info"
                    sx={{ flex: { xs: "1 1 0", md: "0 1 auto" } }}
                  >
                    {audience.loading
                      ? "Counting…"
                      : `${audience.count.toLocaleString()} contact${
                          audience.count !== 1 ? "s" : ""
                        } match your filters` +
                        (excludedIds.size > 0 ? ` (${excludedIds.size} excluded)` : "")}
                  </MDTypography>
                  {!audience.loading && plannedSummary(audience.channels, activeChannels) && (
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      color="info"
                      sx={{ flexBasis: { xs: "100%", md: "auto" } }}
                    >
                      → {plannedSummary(audience.channels, activeChannels)} will be sent
                    </MDTypography>
                  )}
                  {!audience.loading && skippedSummary(audience.channels, activeChannels) && (
                    <MDTypography variant="caption" color="secondary" sx={{ flexBasis: "100%" }}>
                      {skippedSummary(audience.channels, activeChannels)} Each email address and
                      phone number gets one message.
                    </MDTypography>
                  )}
                </MDBox>
              )}

              {/* Recipient list preview */}
              <Collapse in={showList && audience.recipients.length > 0}>
                {excludedIds.size > 0 && (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mt={2}
                    mb={1}
                    flexWrap={{ xs: "wrap", md: "nowrap" }}
                    sx={{
                      background: "rgba(251,92,31,0.06)",
                      border: "1px solid rgba(251,92,31,0.2)",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Icon sx={{ color: "warning.main", fontSize: "16px !important" }}>
                      remove_circle_outline
                    </Icon>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{ color: "warning.main", flex: 1, minWidth: { xs: "60%", md: "auto" } }}
                    >
                      {excludedIds.size} contact{excludedIds.size !== 1 ? "s" : ""} excluded — their
                      email and phone are left out of the send
                    </MDTypography>
                    <MDButton
                      size="small"
                      variant="text"
                      color="info"
                      onClick={selectAll}
                      sx={{
                        fontSize: "0.7rem",
                        py: 0,
                        minHeight: { xs: 36, md: "unset" },
                        ml: { xs: "auto", md: 0 },
                      }}
                    >
                      Re-select all
                    </MDButton>
                  </MDBox>
                )}
                <MDBox
                  sx={{
                    maxHeight: 360,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mt: excludedIds.size > 0 ? 0 : 2,
                  }}
                >
                  {isPhone ? (
                    // Phones: one stacked row per contact instead of a six-column table.
                    <MDBox>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        px={1}
                        py={0.5}
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          bgcolor: "background.paper",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={allSelected}
                          indeterminate={someExcluded}
                          onChange={allSelected ? deselectAll : selectAll}
                        />
                        <MDTypography variant="caption" fontWeight="bold">
                          Select all
                        </MDTypography>
                        <MDTypography variant="caption" color="secondary" ml="auto">
                          Consent: email · SMS
                        </MDTypography>
                      </MDBox>
                      {audience.recipients.map((r, i) => {
                        const included = !excludedIds.has(String(r._id));
                        return (
                          <MDBox
                            key={i}
                            display="flex"
                            alignItems="flex-start"
                            gap={1}
                            px={1}
                            py={1}
                            onClick={() => toggleContact(r._id)}
                            sx={{
                              cursor: "pointer",
                              opacity: included ? 1 : 0.4,
                              bgcolor: included ? "transparent" : "rgba(0,0,0,0.02)",
                              "&:not(:last-of-type)": {
                                borderBottom: "1px solid",
                                borderColor: "divider",
                              },
                            }}
                          >
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={included}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleContact(r._id)}
                            />
                            <MDBox flex={1} minWidth={0} sx={{ overflowWrap: "anywhere" }}>
                              <MDTypography variant="button" fontWeight="medium" display="block">
                                {r.name || "—"}
                              </MDTypography>
                              <MDTypography variant="caption" color="secondary" display="block">
                                {r.email || "—"}
                              </MDTypography>
                              <MDBox
                                display="flex"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={0.75}
                                mt={0.5}
                              >
                                {r.region && (
                                  <Chip
                                    label={r.region.toUpperCase()}
                                    size="small"
                                    sx={{ fontSize: "0.65rem", height: 18 }}
                                  />
                                )}
                                <MDTypography variant="caption" color="secondary">
                                  {SOURCE_LABELS[r.source] || r.source}
                                </MDTypography>
                                <MDBox display="flex" gap={0.5} ml="auto">
                                  <ConsentDot has={r.communicationConsent?.email} />
                                  <ConsentDot has={r.communicationConsent?.sms} />
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={allSelected}
                              indeterminate={someExcluded}
                              onChange={allSelected ? deselectAll : selectAll}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }}>Region</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }} align="center">
                            <Tooltip title="Email · SMS consent">
                              <span>Consent</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {audience.recipients.map((r, i) => {
                          const sid = String(r._id);
                          const included = !excludedIds.has(sid);
                          return (
                            <TableRow
                              key={i}
                              hover
                              onClick={() => toggleContact(r._id)}
                              sx={{
                                cursor: "pointer",
                                opacity: included ? 1 : 0.4,
                                bgcolor: included ? "transparent" : "rgba(0,0,0,0.02)",
                              }}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={included}
                                  onChange={() => toggleContact(r._id)}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.75rem" }}>{r.name || "—"}</TableCell>
                              <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                {r.email || "—"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {r.region ? (
                                  <Chip
                                    label={r.region.toUpperCase()}
                                    size="small"
                                    sx={{ fontSize: "0.65rem", height: 18 }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                                {SOURCE_LABELS[r.source] || r.source}
                              </TableCell>
                              <TableCell align="center">
                                <MDBox display="flex" justifyContent="center" gap={0.5}>
                                  <ConsentDot has={r.communicationConsent?.email} />
                                  <ConsentDot has={r.communicationConsent?.sms} />
                                </MDBox>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                  {audience.count > 50 && (
                    <MDBox px={2} py={1} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                      <MDTypography variant="caption" color="secondary">
                        Showing first 50 of {audience.count.toLocaleString()} contacts. Exclusions
                        only apply to the contacts shown above.
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Collapse>

              <Divider sx={{ mt: 2, mb: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={respectConsent}
                    onChange={(e) => setRespectConsent(e.target.checked)}
                    color="info"
                    size="small"
                  />
                }
                label={
                  <MDTypography variant="button" fontWeight="regular">
                    Respect communication consent — skip contacts who have not opted in for each
                    channel
                  </MDTypography>
                }
              />
            </SectionCard>

            {/* STEP 2 — Channels */}
            <SectionCard title="Channels" icon="send">
              <Grid container spacing={2}>
                {[
                  {
                    key: "email",
                    label: "Email",
                    icon: "email",
                    note: "HTML supported · Sent from our Elite 247 email account",
                  },
                  {
                    key: "sms",
                    label: "SMS",
                    icon: "sms",
                    note: "Plain text · Sent as a text message to their phone",
                  },
                ].map((ch) => (
                  <Grid item xs={12} sm={6} key={ch.key}>
                    <MDBox
                      onClick={() => setChannels((c) => ({ ...c, [ch.key]: !c[ch.key] }))}
                      sx={{
                        border: "1px solid",
                        borderColor: channels[ch.key] ? "info.main" : "divider",
                        borderRadius: 2,
                        p: 2,
                        cursor: "pointer",
                        backgroundColor: channels[ch.key] ? "rgba(26,115,232,0.06)" : "transparent",
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "info.main" },
                      }}
                    >
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ color: channels[ch.key] ? "info.main" : "text.secondary" }}>
                            {ch.icon}
                          </Icon>
                          <MDTypography variant="button" fontWeight="medium">
                            {ch.label}
                          </MDTypography>
                        </MDBox>
                        <Switch
                          checked={channels[ch.key]}
                          color="info"
                          size="small"
                          onChange={() => {}}
                        />
                      </MDBox>
                      <MDTypography variant="caption" color="secondary" display="block" mt={0.5}>
                        {ch.note}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>
            </SectionCard>

            {/* STEP 3 — Email composer */}
            {channels.email && (
              <SectionCard title="Email Content" icon="article">
                <TextField
                  label="Subject line"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g. {{name}}, your spot is almost gone…"
                  helperText="Supports {{placeholder}} variables"
                  sx={{ mb: 2.5 }}
                />
                <TextField
                  label="Email body (HTML supported)"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={12}
                  placeholder={"<p>Hi {{name}},</p>\n\n<p>Just a quick message…</p>"}
                  helperText="Write inner HTML — it will be wrapped in the branded Elite 247 email template automatically."
                  sx={{ "& .MuiInputBase-root": { fontFamily: "monospace", fontSize: "0.8rem" } }}
                />
              </SectionCard>
            )}

            {/* STEP 3 — SMS composer */}
            {channels.sms && (
              <SectionCard title="SMS Content" icon="sms">
                <TextField
                  label="SMS body"
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Hi {{name}}, don't miss your spot — {{bookingLink}}"
                  helperText={`${smsBody.length} chars · ${Math.ceil(
                    (smsBody.length || 1) / SMS_LIMIT
                  )} segment(s). An opt-out link is appended automatically.`}
                />
              </SectionCard>
            )}

            {/* Test send */}
            {activeChannels.length > 0 && (
              <SectionCard title="Send a Test First" icon="science">
                <MDTypography variant="caption" color="secondary" display="block" mb={2}>
                  Sends the email to the address below using your first matching contact&apos;s data
                  — so you see exactly what recipients will receive. Tests are email only: check the
                  SMS text with Preview.
                </MDTypography>
                <MDBox
                  display="flex"
                  flexWrap={{ xs: "wrap", sm: "nowrap" }}
                  gap={1.5}
                  alignItems="flex-start"
                >
                  <TextField
                    label="Test email address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    size="small"
                    type="email"
                    placeholder="you@example.com"
                    sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ fontSize: "18px !important", color: "text.secondary" }}>
                            email
                          </Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={handleTestSend}
                    disabled={testSending || !testEmail}
                    startIcon={testSending ? <CircularProgress size={14} /> : <Icon>send</Icon>}
                    sx={{ whiteSpace: "nowrap", mt: 0.25, minHeight: { xs: 36, md: "2rem" } }}
                  >
                    {testSending ? "Sending…" : "Send Test"}
                  </MDButton>
                </MDBox>
              </SectionCard>
            )}
          </Grid>

          {/* ── RIGHT COLUMN ── */}
          <Grid item xs={12} lg={4}>
            {/* Placeholder reference */}
            <Card sx={{ mb: 2.5 }}>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                  <Icon sx={{ color: "info.main", fontSize: "20px !important" }}>code</Icon>
                  <MDTypography variant="h6" fontWeight="medium">
                    Placeholders
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" flexWrap="wrap" gap={{ xs: 1, md: 0.75 }}>
                  {PLACEHOLDERS.map((p) => (
                    <Tooltip key={p.tag} title={p.desc} arrow>
                      <Chip
                        label={p.tag}
                        size="small"
                        variant="outlined"
                        onClick={() => navigator.clipboard?.writeText(p.tag)}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.7rem",
                          cursor: "pointer",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </MDBox>
                <MDTypography variant="caption" color="secondary" display="block" mt={1.5}>
                  Click a chip to copy it. Values come from the contact record and Settings panel.
                </MDTypography>
              </MDBox>
            </Card>

            {/* Tips */}
            <Card sx={{ mb: 2.5 }}>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                  <Icon sx={{ color: "warning.main", fontSize: "20px !important" }}>
                    tips_and_updates
                  </Icon>
                  <MDTypography variant="h6" fontWeight="medium">
                    Tips
                  </MDTypography>
                </MDBox>
                {[
                  "Always send a test to yourself first to check formatting and placeholders.",
                  "{{currency}} is the UK price for UK contacts (by region, country or +44 phone) and the Nigerian price for everyone else.",
                  "Use the Region filter to send region-specific pricing or time-zone-appropriate messages. Contacts whose country can't be told are left out of a region-filtered send.",
                  "Use the date range filter to target only recent sign-ups for a follow-up sequence.",
                  "Contacts without an email or phone are automatically skipped for that channel.",
                  "SMS segments cost per 160 characters — the opt-out link is appended, so keep the body under ~120 chars.",
                ].map((tip, i) => (
                  <MDBox key={i} display="flex" alignItems="flex-start" gap={1} mb={1.25}>
                    <Icon
                      sx={{
                        fontSize: "15px !important",
                        color: "info.main",
                        mt: "2px",
                        flexShrink: 0,
                      }}
                    >
                      info
                    </Icon>
                    <MDTypography variant="caption" color="secondary" lineHeight={1.5}>
                      {tip}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Sticky action bar ── */}
        <MDBox
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {/* Phones: a full-width bar with the buttons side by side (wrapping if they can't fit). */}
          <Card
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
              px: { xs: 1, md: 2.5 },
              py: { xs: 1, md: 1.5 },
              boxShadow: 6,
              flexDirection: { xs: "row", md: "column" },
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <MDButton
              variant="outlined"
              color="info"
              onClick={handlePreview}
              disabled={previewing || !sources.length || !activeChannels.length}
              startIcon={previewing ? <CircularProgress size={14} /> : <Icon>preview</Icon>}
              sx={actionBarButtonSx}
            >
              {previewing ? "Loading…" : "Preview"}
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend || sending || audience.loading || plannedCount === 0}
              startIcon={
                sending ? <CircularProgress size={14} color="inherit" /> : <Icon>send</Icon>
              }
              sx={actionBarButtonSx}
            >
              {sending
                ? "Sending…"
                : `Send ${plannedCount > 0 ? plannedCount.toLocaleString() : "—"} message${
                    plannedCount !== 1 ? "s" : ""
                  }`}
            </MDButton>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />

      {/* ── Preview dialog ── */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="info">preview</Icon>
            <MDTypography variant="h6" fontWeight="medium">
              Message Preview
            </MDTypography>
          </MDBox>
          {previewData && (
            <MDTypography variant="caption" color="secondary" sx={{ overflowWrap: "anywhere" }}>
              Sample: <strong>{previewData.sampleContact?.name}</strong> (
              {previewData.sampleContact?.email}) · Source:{" "}
              {SOURCE_LABELS[previewData.sampleContact?.source] ||
                previewData.sampleContact?.source}
              · <strong>{previewData.recipientCount?.toLocaleString()}</strong> contacts match
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {unknownPlaceholdersNote(previewData?.unknownPlaceholders) && (
            <Alert severity="warning" sx={{ mb: 2, overflowWrap: "anywhere" }}>
              {unknownPlaceholdersNote(previewData.unknownPlaceholders)}
            </Alert>
          )}
          {previewData && (
            <MDBox>
              {channels.email && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="overline"
                    color="info"
                    fontWeight="bold"
                    display="block"
                    mb={0.75}
                  >
                    Email Subject
                  </MDTypography>
                  <MDBox
                    sx={{
                      background: "rgba(0,0,0,0.04)",
                      borderRadius: 1,
                      p: 1.5,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {previewData.subject || "(empty)"}
                  </MDBox>
                  <MDTypography
                    variant="overline"
                    color="info"
                    fontWeight="bold"
                    display="block"
                    mt={2}
                    mb={0.75}
                  >
                    Email Body
                  </MDTypography>
                  <MDBox
                    component="div"
                    dangerouslySetInnerHTML={{ __html: previewData.emailBody || "(empty)" }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      fontSize: "0.875rem",
                      overflowWrap: "anywhere",
                      "& *": { maxWidth: "100%" },
                    }}
                  />
                </MDBox>
              )}
              {channels.sms && (
                <MDBox>
                  <MDTypography
                    variant="overline"
                    color="info"
                    fontWeight="bold"
                    display="block"
                    mb={0.75}
                  >
                    SMS Body
                  </MDTypography>
                  <MDBox
                    sx={{
                      background: "rgba(0,0,0,0.04)",
                      borderRadius: 1,
                      p: 1.5,
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {previewData.smsBody || "(empty)"}
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setPreviewOpen(false)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Confirm send dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="warning">warning</Icon>
            <MDTypography variant="h6" fontWeight="medium">
              Confirm Bulk Send
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="secondary" mb={1.5}>
            You are about to send{" "}
            <strong>{plannedSummary(audience.channels, activeChannels)}</strong>
            {excludedIds.size > 0 && <> ({excludedIds.size} contacts manually excluded)</>} to
            contacts from:
          </MDTypography>
          <MDBox mb={1.5} display="flex" flexWrap="wrap" gap={0.5}>
            {sources.map((s) => (
              <Chip
                key={s}
                label={SOURCE_LABELS[s] || s}
                size="small"
                color="info"
                variant="outlined"
              />
            ))}
          </MDBox>
          {filterSummary(filter, sources) && (
            <MDBox mb={1.5}>
              <MDTypography variant="caption" color="secondary">
                Filters: {filterSummary(filter, sources)}
              </MDTypography>
            </MDBox>
          )}
          <MDTypography variant="body2" color="secondary" mb={1}>
            Channels: <strong>{activeChannels.join(" + ").toUpperCase()}</strong>.{" "}
            {respectConsent ? (
              "Contacts who have not consented to a channel are not included in the count above."
            ) : (
              <span style={{ color: "#ea4335" }}>
                Consent filtering is OFF — all contacts will receive a message.
              </span>
            )}
          </MDTypography>
          {skippedSummary(audience.channels, activeChannels) && (
            <MDTypography variant="caption" color="secondary" display="block" mb={1}>
              {skippedSummary(audience.channels, activeChannels)}
            </MDTypography>
          )}
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            This action cannot be undone. Ensure you have sent a test and previewed the message
            first.
          </Alert>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleSend}>
            Yes, Send Now
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default BulkMessage;
